import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, DownloadIcon } from 'lucide-react';
import { getTypeformUrl } from '../utils/cookies';
import { BrochureFormPopup } from './BrochureFormPopup';
const projects = [{
  name: 'Sobha Sanctuary',
  developer: 'Sobha',
  image: 'https://cdn.magicpatterns.com/uploads/2YcrbpRwVyRsZWNgdUGcWc/SobhaHeroPage.webp',
  brochure: 'https://ikigaire.ae/wp-content/uploads/2026/03/THE-GROVE-AT-SOBHA-SANCTUARY_compressed.pdf'
}, {
  name: 'The Heights Country Club & Wellness',
  developer: 'Emaar',
  image: 'https://cdn.magicpatterns.com/uploads/5kshVV84damGwb2NLCmwTT/HOMEPAGE_BANNER_1920x1080-1620x832.jpg',
  brochure: 'https://ikigaire.ae/wp-content/uploads/2026/03/THE_HEIGHTS_BROCHURE_compressed.pdf'
}, {
  name: 'Fairmont Residences Solara Tower',
  developer: 'SOL Properties',
  image: 'https://cdn.magicpatterns.com/uploads/kVohtTe9KBdrbmFbGFFeXZ/banner-img-1-1-1.jpg',
  brochure: 'https://ikigaire.ae/wp-content/uploads/2026/03/Fairmont-Residences-Solara-Tower-_compressed.pdf'
}, {
  name: 'Mercedes-Benz City',
  developer: 'Binghatti',
  image: 'https://cdn.magicpatterns.com/uploads/vjpFFyexDBZByWJdt7EXSS/43ecba247def040.webp',
  brochure: 'https://ikigaire.ae/wp-content/uploads/2026/03/mbp-bc-brochure_compressed.pdf'
}];
export const FeaturedProjects = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [showBrochurePopup, setShowBrochurePopup] = useState(false);
  const [selectedBrochureUrl, setSelectedBrochureUrl] = useState('');
  const next = useCallback(() => {
    setActive(prev => (prev + 1) % projects.length);
  }, []);
  const prev = useCallback(() => {
    setActive(prev => (prev - 1 + projects.length) % projects.length);
  }, []);
  useEffect(() => {
    if (paused) return;
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [paused, next]);
  const handleDownload = (e: React.MouseEvent, brochureUrl: string) => {
    e.preventDefault();
    setSelectedBrochureUrl(brochureUrl);
    setShowBrochurePopup(true);
  };
  const current = projects[active];
  const progressPercent = (active + 1) / projects.length * 100;
  return <section className="bg-gray-50 py-16 md:py-24" data-id="element-249">
      <div className="container mx-auto px-4 md:px-8" data-id="element-250">
        <div className="max-w-5xl mx-auto" data-id="element-251">
          {/* Header */}
          <div className="text-center mb-10 md:mb-14" data-id="element-252">
            <span className="text-[#d8a34d] text-xs md:text-sm font-semibold tracking-[0.2em] uppercase mb-4 block" data-id="element-253">
              Curated Opportunities
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-[#1a1a1a] leading-tight" data-id="element-254">
              Featured Projects
            </h2>
          </div>

          {/* Carousel wrapper — relative for arrow positioning */}
          <div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-id="element-255">
            {/* Left Arrow — outside card, half overlapping */}
            <button onClick={prev} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 w-12 h-12 rounded-full bg-white border-2 border-[#d8a34d] flex items-center justify-center text-[#1a2e1c] hover:bg-[#fdf6e9] transition-colors shadow-[0_4px_16px_rgba(0,0,0,0.12)]" aria-label="Previous project" data-id="element-256">
              <ChevronLeftIcon className="w-6 h-6" data-id="element-257" />
            </button>

            {/* Right Arrow — outside card, half overlapping */}
            <button onClick={next} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-20 w-12 h-12 rounded-full bg-white border-2 border-[#d8a34d] flex items-center justify-center text-[#1a2e1c] hover:bg-[#fdf6e9] transition-colors shadow-[0_4px_16px_rgba(0,0,0,0.12)]" aria-label="Next project" data-id="element-258">
              <ChevronRightIcon className="w-6 h-6" data-id="element-259" />
            </button>

            {/* Showcase Card */}
            <div className="rounded-2xl overflow-hidden shadow-lg" data-id="element-260">
              {/* Images — all stacked, only active visible */}
              <div className="relative h-72 md:h-96" data-id="element-261">
                {projects.map((project, index) => <img key={index} src={project.image} alt={project.name} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out" style={{
                opacity: index === active ? 1 : 0
              }} data-id="element-262" />)}

                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent pointer-events-none" data-id="element-263" />

                {/* Content overlay */}
                <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-10" data-id="element-264">
                  {/* Developer badge */}
                  <span className="text-[#d8a34d] text-[10px] md:text-xs font-bold tracking-[0.15em] uppercase mb-2" data-id="element-265">
                    {current.developer}
                  </span>

                  {/* Project name */}
                  <h3 className="text-2xl md:text-3xl lg:text-4xl font-display font-bold text-white leading-tight mb-5 md:mb-6 max-w-lg" data-id="element-266">
                    {current.name}
                  </h3>

                  {/* Download button */}
                  <div data-id="element-267">
                    <a href={current.brochure} onClick={e => handleDownload(e, current.brochure)} className="inline-flex items-center gap-2 px-5 py-2.5 md:px-6 md:py-3 rounded-lg font-semibold text-sm bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 transition-colors" data-id="element-268">
                      <DownloadIcon className="w-4 h-4" data-id="element-269" />
                      Download Brochure
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6 px-1" data-id="element-270">
            <div className="w-full h-1 bg-gray-200 rounded-full overflow-hidden" data-id="element-271">
              <div className="h-full bg-[#d8a34d] rounded-full transition-all duration-500 ease-in-out" style={{
              width: `${progressPercent}%`
            }} data-id="element-272" />
            </div>
            <p className="text-center text-sm text-gray-500 font-medium mt-3 tabular-nums" data-id="element-273">
              {active + 1} / {projects.length}
            </p>
          </div>
        </div>
      </div>
      <BrochureFormPopup isOpen={showBrochurePopup} onClose={() => setShowBrochurePopup(false)} brochureUrl={selectedBrochureUrl} data-id="element-274" />
    </section>;
};